import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, IconButton, Button, Stack, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { mergeDataGridStyles } from '../../styles/dataGridStyles';
import {
    getLocalSessions, deleteLocalSession, pendingOpsCountForSession,
} from '../../services/trainingSessionService';

export default function TrainingSessionsList() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadLocal = useCallback(async () => {
        setLoading(true);
        const local = await getLocalSessions();
        // Local-only IDB list — closed сессии уходят отсюда в момент close().
        // На всякий случай ещё раз отфильтруем (если каким-то образом залезли).
        setSessions((local || []).filter(s => s.status !== 'closed'));
        setLoading(false);
    }, []);

    useEffect(() => { loadLocal(); }, [loadLocal]);

    // Убирает сессию только с этого айпада — в админке Symfony она и вся её
    // история остаются. Обратно не приедет: trainingSessions не синкается с сервера.
    const handleDelete = async (row) => {
        const pending = await pendingOpsCountForSession(row.uuid);
        const warning = pending > 0
            ? `\n\nWarning: ${pending} change(s) have not reached the server yet and will be lost.`
            : '';
        const msg = `Remove "${row.courseName}" from this iPad?\n\n`
            + 'It stays in the admin panel with all its history — this only hides it here.'
            + warning;
        if (!window.confirm(msg)) return;

        await deleteLocalSession(row.uuid);
        await loadLocal();
    };

    const columns = [
        {
            field: 'courseName',
            headerName: 'Course',
            width: 280,
            valueGetter: (value, row) => row?.courseName,
            renderCell: ({ row }) => (
                <Typography variant="body2" fontWeight={500}
                    sx={{ whiteSpace: 'normal', lineHeight: 1.3, py: 0.5 }}>
                    {row.courseName}
                </Typography>
            ),
        },
        {
            field: 'defaultLanguage',
            headerName: 'Lang',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            valueGetter: (value, row) => row?.defaultLanguage?.toUpperCase() || '—',
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 100,
            sortable: false,
            renderCell: ({ value }) => <StatusChip status={value} />,
        },
        {
            field: 'progress',
            headerName: 'Progress',
            width: 160,
            sortable: false,
            renderCell: ({ row }) => {
                const p = row.participants || { total: 0, sent: 0, completed: 0 };
                if (p.total === 0) {
                    return <Typography variant="caption" color="text.secondary">empty</Typography>;
                }
                const fullyDone = p.completed === p.total;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, lineHeight: 1.2, py: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            <Box component="span" sx={{ color: 'text.primary' }}>{p.total}</Box>
                            <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>invited</Box>
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            <Box component="span" sx={{ color: p.sent > 0 ? 'info.main' : 'text.disabled' }}>{p.sent}</Box>
                            <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>sent</Box>
                            <Box component="span" sx={{ mx: 0.75, color: 'text.disabled', fontWeight: 400 }}>·</Box>
                            <Box component="span" sx={{
                                color: fullyDone ? 'success.main' : (p.completed > 0 ? 'warning.main' : 'text.disabled'),
                            }}>{p.completed}</Box>
                            <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>done</Box>
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            flex: 1,
            minWidth: 120,
            sortable: false,
            valueGetter: (value, row) => row?.createdAt
                ? new Date(row.createdAt).toLocaleDateString()
                : '—',
        },
        {
            field: 'actions',
            headerName: '',
            width: 64,
            sortable: false,
            filterable: false,
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }) => (
                <Tooltip title="Remove from this iPad (stays in admin panel)">
                    <IconButton sx={{ width: 44, height: 44, color: '#f44336' }}
                        onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <Box sx={{
            position: 'fixed',
            top: '64px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
        }}>
            <Box sx={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 2,
                flex: '0 0 auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/main')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>Training Sessions</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Demo · {sessions.length} session{sessions.length === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/training-sessions/new')}
                    sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                >
                    New
                </Button>
            </Box>

            <Box sx={{ flex: '1 1 auto', overflow: 'hidden', backgroundColor: 'white' }}>
                <DataGrid
                    rows={sessions}
                    columns={columns}
                    loading={loading}
                    getRowId={row => row.uuid}
                    getRowHeight={() => 'auto'}
                    columnHeaderHeight={44}
                    onRowClick={(params) => navigate(`/training-sessions/${params.row.uuid}`)}
                    initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                    pageSizeOptions={[25, 50, 100]}
                    disableSelectionOnClick
                    disableColumnMenu
                    hideFooter={sessions.length <= 25}
                    sx={mergeDataGridStyles({
                        border: 'none',
                        '& .MuiDataGrid-row:hover': {
                            cursor: 'pointer',
                            backgroundColor: 'action.hover',
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '14px',
                            paddingRight: '14px',
                            borderBottom: '1px solid #f0f0f0',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            paddingLeft: '14px',
                            paddingRight: '14px',
                            backgroundColor: '#fafafa',
                        },
                    })}
                    localeText={{ noRowsLabel: 'No sessions yet. Tap "New session" to create one.' }}
                />
            </Box>
        </Box>
    );
}

function StatusChip({ status }) {
    const map = {
        draft: { label: 'Draft', color: 'default' },
        active: { label: 'Active', color: 'success' },
        closed: { label: 'Closed', color: 'warning' },
    };
    const cfg = map[status] || { label: status || '—', color: 'default' };
    return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />;
}
